import React from 'react';

export type ColorDef = { id: string; name: string; hex: string };

interface Props {
  colors: ColorDef[];
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  addColor: (name: string, hex: string) => void;
  updateColor: (id: string, name: string, hex: string) => void;
  removeColor: (id: string) => void;
  gridBg: string;
  setGridBg: (hex: string) => void;
  onHoverColor?: (id: string | null) => void;
  dark?: boolean;
}

export default function ColorPalette(props: Props){
  const [newName, setNewName] = React.useState('Colour');
  const [newHex, setNewHex] = React.useState('#6c5ce7');
  const [localName, setLocalName] = React.useState('');
  const [localHex, setLocalHex] = React.useState('#000000');
  const debounceRef = React.useRef<number | null>(null);
  const [localGridBg, setLocalGridBg] = React.useState(props.gridBg);
  const gridBgDebounceRef = React.useRef<number | null>(null);

  React.useEffect(()=>{
    // when selection changes, populate local inputs
    if(props.selectedId){
      const def = props.colors.find(p=>p.id===props.selectedId);
      if(def){ setLocalName(def.name); setLocalHex(def.hex); }
    }
  },[props.selectedId]);

  // sync localGridBg when prop changes
  React.useEffect(()=>{
    setLocalGridBg(props.gridBg);
  },[props.gridBg]);

  // cleanup debounce on unmount
  React.useEffect(()=>{
    return ()=>{
      if(gridBgDebounceRef.current) window.clearTimeout(gridBgDebounceRef.current);
    };
  },[props.selectedId]);

  // helper to commit debounced update
  function commitUpdate(id: string){
    if(!id) return;
    if(debounceRef.current){ window.clearTimeout(debounceRef.current); debounceRef.current = null; }
    const def = props.colors.find(p=>p.id===id);
    if(def && (def.name !== localName || def.hex !== localHex)){
      props.updateColor(id, localName, localHex);
    }
  }

  return (
    <div className="color-palette" style={{width:'100%',height:'100vh',padding:12,boxSizing:'border-box',display:'flex',flexDirection:'column'}}>
      <h3 style={{margin:'0 0 8px 0'}}>Palette</h3>
      <button onClick={() => {
        const selectedDef = props.selectedId ? props.colors.find(p => p.id === props.selectedId) : undefined;
        const hexToUse = selectedDef ? selectedDef.hex : newHex;
        props.addColor(newName || `Color ${props.colors.length+1}`, hexToUse);
        setNewName('Color');
        setNewHex('#6c5ce7');
      }} style={{background:'#4caf50',color:'#fff',border:'none',padding:'8px 12px',borderRadius:12,fontWeight:600, marginBottom: '2rem'}}>Add Colour</button>
      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
        {props.colors.map(c=> (
          <div key={c.id} style={{display:'flex',flexDirection:'column',alignItems:'center',width:56}}>
            <div
              onMouseEnter={() => props.onHoverColor && props.onHoverColor(c.id)}
              onMouseLeave={() => props.onHoverColor && props.onHoverColor(null)}
              onClick={()=>props.setSelectedId(c.id)}
              title={c.name}
              style={{width:48,height:48,background:c.hex,border: props.selectedId===c.id ? '4px solid #2196f3' : '1px solid rgba(0,0,0,0.12)', cursor:'pointer', borderRadius:6}}
            />
            <div style={{fontSize:11,marginTop:4,textAlign:'center',width:'100%',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{c.name}</div>
          </div>
        ))}
      </div>
      <div style={{width:'100%',height:8}} />
      <div style={{width:'100%',borderTop:'1px solid rgba(0,0,0,0.06)',marginTop:8,paddingTop:12}}>
        <h4 style={{margin:'8px 0 6px 0'}}>Edit Selected</h4>
        {props.selectedId ? (()=>{
          const def = props.colors.find(p=>p.id===props.selectedId);
          if(!def) return <div>No selection</div>;
          return (
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              <label style={{fontSize:12,opacity:0.8}}>Name</label>
              <input value={localName} onChange={e=>{
                    setLocalName(e.target.value);
                    if(debounceRef.current) window.clearTimeout(debounceRef.current);
                    debounceRef.current = window.setTimeout(()=>commitUpdate(def.id), 350);
                  }} onKeyDown={e=>{ if(e.key === 'Enter') commitUpdate(def.id); }} style={{width:'100%',padding:'6px',boxSizing:'border-box'}} />

                  <label style={{fontSize:12,opacity:0.8}}>Colour</label>
                  <input type="color" value={localHex} onChange={e=>{
                    setLocalHex(e.target.value);
                    if(debounceRef.current) window.clearTimeout(debounceRef.current);
                    debounceRef.current = window.setTimeout(()=>commitUpdate(def.id), 350);
                  }} onBlur={()=>commitUpdate(def.id)} style={{width:'100%',height:40,padding:0,border:'none',background:'transparent'}} />

              <div style={{display:'flex',justifyContent:'center'}}>
                <button onClick={()=>{
                    const ok = window.confirm(`Delete color "${def.name}"? This cannot be undone.`);
                    if(ok) props.removeColor(def.id);
                  }} style={{background:'#ef5350',color:'#fff',border:'none',padding:'6px 10px',borderRadius:6}}>Delete</button>
              </div>
            </div>
          );
        })() : <div>Select a color to edit</div>}
      </div>
      <div style={{marginTop:'auto',display:'flex',gap:8,alignItems:'center',flexDirection:'column'}}>
        {/* <input value={newName} onChange={e=>setNewName(e.target.value)} placeholder="Name" style={{flex:1}} />
        <input type="color" value={newHex} onChange={e=>setNewHex(e.target.value)} /> */}
        

        <div style={{width:'100%',marginTop:12}}>
          <h4 style={{margin:'8px 0 6px 0'}}>Grid Background</h4>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            <input type="color" value={localGridBg} onChange={e=>{
              const v = e.target.value;
              setLocalGridBg(v);
              if(gridBgDebounceRef.current) window.clearTimeout(gridBgDebounceRef.current);
              gridBgDebounceRef.current = window.setTimeout(()=>{
                props.setGridBg(v);
                gridBgDebounceRef.current = null;
              }, 150) as unknown as number;
            }} onBlur={()=>{
              if(gridBgDebounceRef.current) { window.clearTimeout(gridBgDebounceRef.current); gridBgDebounceRef.current = null; }
              props.setGridBg(localGridBg);
            }} style={{width:'100%',height:36,border:'none',padding:0,background:'transparent'}} disabled={!!props.dark} />
            {props.dark ? <div style={{fontSize:12,opacity:0.7}}>Disabled in dark mode</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}
